import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ShoppingCart, ArrowLeft, CreditCard, QrCode, CheckCircle, Image as ImageIcon } from 'lucide-react';
import { Button, Input, useToast, PhoneInput } from '@caruso/ui';
import { useCart } from '@/contexts/cart-context';
import { storeService } from '@/services/store.service';
import { paymentsService, CreditCardData, CreditCardHolderInfo } from '@/services/payments.service';
import { StoreLayout } from '@/components/layouts/store-layout';
import { PixPayment } from '@/components/payment/pix-payment';
import { CreditCardPayment } from '@/components/payment/credit-card-payment';
import axios from 'axios';
import 'react-phone-number-input/style.css';

interface FormData {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerDocument?: string;
  zipCode: string;
  address: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
}

type PaymentMethod = 'pix' | 'credit_card' | null;
type CheckoutStep = 'customer-info' | 'payment' | 'success';

// Componente para exibir imagem do produto com fallback
function ProductImage({ src, alt }: { src?: string; alt: string }) {
  const [imageError, setImageError] = useState(false);
  const hasImage = src && !imageError;

  return (
    <div className="w-16 h-16 rounded overflow-hidden bg-brand-smoke/20 border border-brand-gold/20 flex items-center justify-center flex-shrink-0">
      {hasImage ? (
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      ) : (
        <ImageIcon className="w-8 h-8 text-brand-gold/40" />
      )}
    </div>
  );
}

export function StoreCheckoutPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { items, totalPrice, clearCart } = useCart();
  const [currentStep, setCurrentStep] = useState<CheckoutStep>('customer-info');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(null);
  const [createdOrderId, setCreatedOrderId] = useState<string | null>(null);
  const [pixData, setPixData] = useState<any>(null);
  const [isLoadingCep, setIsLoadingCep] = useState(false);
  const [isQuickSale, setIsQuickSale] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerDocument: '',
    zipCode: '',
    address: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
  });

  // Carregar dados do cliente da venda rápida se existirem
  useEffect(() => {
    const quickSaleClient = localStorage.getItem('@caruso:quick-sale-client');
    if (quickSaleClient) {
      try {
        const clientData = JSON.parse(quickSaleClient);
        setFormData(clientData);
        setIsQuickSale(true); // Marcar como venda rápida/admin
        localStorage.removeItem('@caruso:quick-sale-client');
      } catch (error) {
        console.error('Erro ao carregar dados do cliente:', error);
      }
    }
  }, []);

  const createOrderMutation = useMutation({
    mutationFn: storeService.createOrder,
    onSuccess: (order) => {
      setCreatedOrderId(order.id);
      setCurrentStep('payment');
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao criar pedido',
        description: error.response?.data?.message || 'Tente novamente mais tarde',
        variant: 'destructive',
      });
    },
  });

  const createPixPaymentMutation = useMutation({
    mutationFn: paymentsService.createPixPayment,
    onSuccess: (data) => {
      setPixData(data.pix);
      toast({
        title: 'PIX gerado com sucesso!',
        description: 'Escaneie o QR Code ou copie o código para pagar',
        variant: 'success',
      });
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error ||
                          error.message || 
                          'Erro ao gerar PIX. Verifique se todos os dados do pedido estão corretos.';
      console.error('Erro ao gerar PIX:', error);
      toast({
        title: 'Erro ao gerar PIX',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  const createCreditCardPaymentMutation = useMutation({
    mutationFn: async ({
      orderId,
      creditCard,
      holderInfo,
      installments,
    }: {
      orderId: string;
      creditCard: CreditCardData;
      holderInfo: CreditCardHolderInfo;
      installments: number;
    }) => {
      return paymentsService.createCreditCardPayment(
        orderId,
        creditCard,
        holderInfo,
        installments
      );
    },
    onSuccess: (data) => {
      clearCart();
      setCurrentStep('success');
      toast({
        title: 'Pagamento processado!',
        description: 'Seu pedido foi confirmado com sucesso',
        variant: 'success',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao processar pagamento',
        description: error.response?.data?.message || 'Verifique os dados do cartão',
        variant: 'destructive',
      });
    },
  });

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  const formatCEP = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{5})(\d)/, '$1-$2').slice(0, 9);
  };

  const formatDocument = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      // CPF: 000.000.000-00
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
        .slice(0, 14);
    } else {
      // CNPJ: 00.000.000/0000-00
      return numbers
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
        .slice(0, 18);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    let formattedValue = value;
    if (field === 'zipCode') {
      formattedValue = formatCEP(value);
    } else if (field === 'customerDocument') {
      formattedValue = formatDocument(value);
    }
    setFormData((prev) => ({ ...prev, [field]: formattedValue }));
  };

  const handlePhoneChange = (value: string | undefined) => {
    setFormData((prev) => ({ ...prev, customerPhone: value || '' }));
  };

  const handleCepBlur = async () => {
    const cep = formData.zipCode.replace(/\D/g, '');
    if (cep.length !== 8) return;

    setIsLoadingCep(true);
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
      if (response.data.erro) {
        toast({
          title: 'CEP não encontrado',
          variant: 'destructive',
        });
        return;
      }

      setFormData((prev) => ({
        ...prev,
        address: response.data.logradouro || '',
        neighborhood: response.data.bairro || '',
        city: response.data.localidade || '',
        state: response.data.uf || '',
      }));
    } catch (error) {
      toast({
        title: 'Erro ao buscar CEP',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingCep(false);
    }
  };

  const handleCustomerInfoSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.customerName || !formData.customerEmail || !formData.customerPhone) {
      toast({
        title: 'Preencha todos os campos obrigatórios',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.zipCode || !formData.address || !formData.number || !formData.neighborhood || !formData.city || !formData.state) {
      toast({
        title: 'Preencha todos os campos de endereço',
        variant: 'destructive',
      });
      return;
    }

    createOrderMutation.mutate({
      ...formData,
      customerPhone: formData.customerPhone, // Já vem em formato E.164 do PhoneInput
      customerDocument: formData.customerDocument?.replace(/\D/g, '') || undefined,
      zipCode: formData.zipCode.replace(/\D/g, ''),
      items: items.map((item) => ({
        productId: item.id,
        quantity: item.quantity,
      })),
      isAdminOrder: isQuickSale, // Permite produtos fora da loja em vendas rápidas/admin
    });
  };

  const handlePaymentMethodSelect = (method: PaymentMethod) => {
    setPaymentMethod(method);

    if (method === 'pix' && createdOrderId) {
      createPixPaymentMutation.mutate(createdOrderId);
    }
  };

  const handleCreditCardSubmit = async (
    creditCard: CreditCardData,
    holderInfo: CreditCardHolderInfo,
    installments: number
  ) => {
    if (!createdOrderId) return;

    await createCreditCardPaymentMutation.mutateAsync({
      orderId: createdOrderId,
      creditCard,
      holderInfo,
      installments,
    });
  };

  if (items.length === 0) {
    navigate('/loja');
    return null;
  }

  return (
    <StoreLayout>
      <div className="space-y-6">
        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className={`flex items-center gap-2 ${currentStep === 'customer-info' ? 'text-brand-gold' : 'text-brand-ivory/40'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${currentStep === 'customer-info' ? 'border-brand-gold bg-brand-gold/10' : 'border-brand-ivory/40'}`}>
              1
            </div>
            <span className="hidden sm:inline">Dados</span>
          </div>
          <div className="w-16 h-0.5 bg-brand-gold/20"></div>
          <div className={`flex items-center gap-2 ${currentStep === 'payment' ? 'text-brand-gold' : currentStep === 'success' ? 'text-brand-gold' : 'text-brand-ivory/40'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${currentStep === 'payment' ? 'border-brand-gold bg-brand-gold/10' : currentStep === 'success' ? 'border-brand-gold bg-brand-gold/10' : 'border-brand-ivory/40'}`}>
              {currentStep === 'success' ? <CheckCircle className="w-5 h-5" /> : '2'}
            </div>
            <span className="hidden sm:inline">Pagamento</span>
          </div>
        </div>

        {/* Step 1: Customer Info */}
        {currentStep === 'customer-info' && (
          <>
            <Button variant="ghost" onClick={() => navigate('/loja/carrinho')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar ao carrinho
            </Button>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <form onSubmit={handleCustomerInfoSubmit} className="lg:col-span-2 space-y-6">
                {/* Customer Information */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-brand-ink/50 border border-brand-gold/20 rounded-lg p-6"
                >
                  <h2 className="text-xl font-serif text-brand-gold mb-4">Informações Pessoais</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-brand-ivory mb-2">Nome Completo *</label>
                      <Input
                        value={formData.customerName}
                        onChange={(e) => handleInputChange('customerName', e.target.value)}
                        placeholder="Seu nome completo"
                        required
                        className="bg-brand-smoke/50 border-brand-gold/20"
                      />
                    </div>
                    <div>
                      <label className="block text-brand-ivory mb-2">E-mail *</label>
                      <Input
                        type="email"
                        value={formData.customerEmail}
                        onChange={(e) => handleInputChange('customerEmail', e.target.value)}
                        placeholder="seu@email.com"
                        required
                        className="bg-brand-smoke/50 border-brand-gold/20"
                      />
                    </div>
                    <div>
                      <label className="block text-brand-ivory mb-2">Telefone *</label>
                      <PhoneInput
                        value={formData.customerPhone}
                        onChange={handlePhoneChange}
                        defaultCountry="BR"
                        placeholder="Digite o telefone"
                      />
                    </div>
                    <div>
                      <label className="block text-brand-ivory mb-2">CPF/CNPJ</label>
                      <Input
                        value={formData.customerDocument || ''}
                        onChange={(e) => handleInputChange('customerDocument', e.target.value)}
                        placeholder="000.000.000-00 ou 00.000.000/0000-00"
                        className="bg-brand-smoke/50 border-brand-gold/20"
                      />
                    </div>
                  </div>
                </motion.div>

                {/* Delivery Address */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-brand-ink/50 border border-brand-gold/20 rounded-lg p-6"
                >
                  <h2 className="text-xl font-serif text-brand-gold mb-4">Endereço de Entrega</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-brand-ivory mb-2">CEP *</label>
                      <Input
                        value={formData.zipCode}
                        onChange={(e) => handleInputChange('zipCode', e.target.value)}
                        onBlur={handleCepBlur}
                        placeholder="00000-000"
                        required
                        disabled={isLoadingCep}
                        className="bg-brand-smoke/50 border-brand-gold/20"
                      />
                    </div>
                    <div>
                      <label className="block text-brand-ivory mb-2">Número *</label>
                      <Input
                        value={formData.number}
                        onChange={(e) => handleInputChange('number', e.target.value)}
                        placeholder="123"
                        required
                        className="bg-brand-smoke/50 border-brand-gold/20"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-brand-ivory mb-2">Rua/Avenida *</label>
                      <Input
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        placeholder="Nome da rua"
                        required
                        className="bg-brand-smoke/50 border-brand-gold/20"
                      />
                    </div>
                    <div>
                      <label className="block text-brand-ivory mb-2">Complemento</label>
                      <Input
                        value={formData.complement}
                        onChange={(e) => handleInputChange('complement', e.target.value)}
                        placeholder="Apto, bloco, etc"
                        className="bg-brand-smoke/50 border-brand-gold/20"
                      />
                    </div>
                    <div>
                      <label className="block text-brand-ivory mb-2">Bairro *</label>
                      <Input
                        value={formData.neighborhood}
                        onChange={(e) => handleInputChange('neighborhood', e.target.value)}
                        placeholder="Bairro"
                        required
                        className="bg-brand-smoke/50 border-brand-gold/20"
                      />
                    </div>
                    <div>
                      <label className="block text-brand-ivory mb-2">Cidade *</label>
                      <Input
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        placeholder="Cidade"
                        required
                        className="bg-brand-smoke/50 border-brand-gold/20"
                      />
                    </div>
                    <div>
                      <label className="block text-brand-ivory mb-2">Estado *</label>
                      <Input
                        value={formData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                        placeholder="UF"
                        maxLength={2}
                        required
                        className="bg-brand-smoke/50 border-brand-gold/20"
                      />
                    </div>
                  </div>
                </motion.div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={createOrderMutation.isPending}
                >
                  {createOrderMutation.isPending ? 'Processando...' : 'Continuar para pagamento'}
                </Button>
              </form>

              {/* Order Summary */}
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-brand-ink/50 border border-brand-gold/20 rounded-lg p-6 sticky top-6"
                >
                  <h2 className="text-xl font-serif text-brand-gold mb-4 flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5" />
                    Resumo do Pedido
                  </h2>
                  <div className="space-y-4">
                    {items.map((item) => (
                      <div key={item.id} className="flex gap-4">
                        <ProductImage src={item.image} alt={item.name} />
                        <div className="flex-1">
                          <p className="text-brand-ivory font-medium">{item.name}</p>
                          <p className="text-brand-ivory/60 text-sm">
                            {item.quantity}x {formatPrice(item.price)}
                          </p>
                        </div>
                        <p className="text-brand-gold font-medium">
                          {formatPrice(item.price * item.quantity)}
                        </p>
                      </div>
                    ))}
                    <div className="border-t border-brand-gold/20 pt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-xl font-serif text-brand-gold">Total</span>
                        <span className="text-2xl font-bold text-brand-gold">
                          {formatPrice(totalPrice)}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </>
        )}

        {/* Step 2: Payment */}
        {currentStep === 'payment' && (
          <div className="max-w-4xl mx-auto space-y-6">
            <div>
              <h1 className="text-3xl font-serif text-brand-gold mb-2">Escolha a forma de pagamento</h1>
              <p className="text-brand-ivory/60">Total: {formatPrice(totalPrice)}</p>
            </div>

            {/* Payment Method Selection */}
            {!paymentMethod && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => handlePaymentMethodSelect('pix')}
                  disabled={createPixPaymentMutation.isPending}
                  className="bg-brand-ink/50 border-2 border-brand-gold/20 hover:border-brand-gold rounded-lg p-6 transition-all hover:scale-105"
                >
                  <QrCode className="w-12 h-12 text-brand-gold mx-auto mb-4" />
                  <h3 className="text-xl font-serif text-brand-gold mb-2">PIX</h3>
                  <p className="text-brand-ivory/60 text-sm">
                    {createPixPaymentMutation.isPending ? 'Gerando PIX...' : 'Pagamento instantâneo'}
                  </p>
                </button>

                <button
                  onClick={() => handlePaymentMethodSelect('credit_card')}
                  className="bg-brand-ink/50 border-2 border-brand-gold/20 hover:border-brand-gold rounded-lg p-6 transition-all hover:scale-105"
                >
                  <CreditCard className="w-12 h-12 text-brand-gold mx-auto mb-4" />
                  <h3 className="text-xl font-serif text-brand-gold mb-2">Cartão de Crédito</h3>
                  <p className="text-brand-ivory/60 text-sm">
                    Parcelamento em até 12x
                  </p>
                </button>
              </div>
            )}

            {/* PIX Payment */}
            {paymentMethod === 'pix' && pixData && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-brand-ink/50 border border-brand-gold/20 rounded-lg p-6"
              >
                <Button
                  variant="ghost"
                  onClick={() => {
                    setPaymentMethod(null);
                    setPixData(null);
                  }}
                  className="mb-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar
                </Button>
                <PixPayment pixData={pixData} />
              </motion.div>
            )}

            {/* Credit Card Payment */}
            {paymentMethod === 'credit_card' && createdOrderId && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-brand-ink/50 border border-brand-gold/20 rounded-lg p-6"
              >
                <Button
                  variant="ghost"
                  onClick={() => setPaymentMethod(null)}
                  className="mb-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar
                </Button>
                <CreditCardPayment
                  onSubmit={handleCreditCardSubmit}
                  totalAmount={totalPrice}
                  isProcessing={createCreditCardPaymentMutation.isPending}
                />
              </motion.div>
            )}
          </div>
        )}

        {/* Step 3: Success */}
        {currentStep === 'success' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-2xl mx-auto text-center py-12"
          >
            <div className="bg-brand-gold/20 border-2 border-brand-gold w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-brand-gold" />
            </div>
            <h1 className="text-3xl font-serif text-brand-gold mb-4">
              Pagamento Confirmado!
            </h1>
            <p className="text-brand-ivory/80 mb-8">
              Seu pedido foi processado com sucesso. Você receberá um e-mail com os detalhes.
            </p>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate('/loja')} variant="outline">
                Voltar para a loja
              </Button>
              <Button onClick={() => navigate(`/loja/pedido/${createdOrderId}`)} variant="premium">
                Ver detalhes do pedido
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </StoreLayout>
  );
}
